/**
 * What job postings actually say about sponsorship — read from their own
 * words, not inferred from a company's reputation or a checkbox somewhere.
 *
 * ── The point the viewer has to get FIRST ─────────────────────────────────
 * Almost no posting says anything at all. If the diagram opened with the
 * three-way split, a reader would walk away thinking "62% refuse" is a fact
 * about the job market, when it is actually a fact about a tiny, self-selected
 * sliver of it — the postings that bothered to say anything. So the ratio
 * comes first, in its own line, before the composition bar even appears.
 *
 * ── Why "not stated" is not a fourth bar segment ──────────────────────────
 * A bar with four segments where one segment is 98.6% of the width is not a
 * comparison, it's a single pixel next to a wall. Worse, it invites the
 * reader to read "not stated" as a soft "no" — and that is the one inference
 * this product refuses to make on the poster's behalf. So the bar holds only
 * the postings that actually took a position, and the silence is named in
 * words beside it instead of drawn as a shape.
 *
 * ── The one emphasis ──────────────────────────────────────────────────────
 * `no_sponsorship` gets `flag` amber — the same ink GroundingBadge uses for
 * "check this" elsewhere in the product — because that is the only segment
 * that changes what someone should do next if they need sponsorship. The
 * other two stances are true and worth reading, but they are not the
 * decision point, so they stay in stone.
 */
const STANCES = [
  { key: "no_sponsorship", label: "No sponsorship", emphasis: true },
  { key: "citizenship_required", label: "Citizenship required", emphasis: false, swatch: "bg-stone-700" },
  { key: "sponsorship_available", label: "Sponsorship available", emphasis: false, swatch: "bg-stone-400" },
];

const fmt = (n) => n.toLocaleString("en-US");
const pct = (part, whole) => (whole > 0 ? Math.round((part / whole) * 100) : 0);

export default function SponsorshipTruth({ counts }) {
  const not_stated = counts?.not_stated ?? 0;
  const no_sponsorship = counts?.no_sponsorship ?? 0;
  const citizenship_required = counts?.citizenship_required ?? 0;
  const sponsorship_available = counts?.sponsorship_available ?? 0;
  const total = not_stated + no_sponsorship + citizenship_required + sponsorship_available;

  // No data yet, or a props shape that sums to nothing real — draw nothing
  // rather than a chart of zeroes, which would read as "no one is refusing"
  // instead of "we have no answer yet".
  if (!counts || !(total > 0)) return null;

  const values = { no_sponsorship, citizenship_required, sponsorship_available };
  const stated = no_sponsorship + citizenship_required + sponsorship_available;
  // Rounded down deliberately: "1 in 74.1" is a formula, "1 in 74" is a fact
  // you can repeat. `stated` is guarded because a healthy index can still
  // have a sweep where nothing has been classified yet.
  const ratio = stated > 0 ? Math.round(total / stated) : null;

  const segments = STANCES.map((s) => ({
    ...s,
    count: values[s.key],
    // Real widths, not rounded ones — position/length here is the actual
    // encoding, and rounding it would make the bar lie by a pixel or two
    // in service of a percentage that only appears in the label anyway.
    widthPct: stated > 0 ? (values[s.key] / stated) * 100 : 0,
    displayPct: pct(values[s.key], stated),
  }));

  const textEquivalent =
    `Of ${fmt(total)} postings NextOpp has read, ${fmt(not_stated)} (${pct(not_stated, total)}%) say nothing ` +
    `about sponsorship. Of the ${fmt(stated)} that do, ${fmt(no_sponsorship)} (${segments[0].displayPct}%) say ` +
    `no sponsorship, ${fmt(citizenship_required)} (${segments[1].displayPct}%) require citizenship, and ` +
    `${fmt(sponsorship_available)} (${segments[2].displayPct}%) say sponsorship is available.`;

  return (
    <figure className="m-0" data-testid="sponsorship-truth">
      {/* The whole graphic in one sentence, for anyone not looking at it —
          and the only place all four raw counts live together. */}
      <figcaption className="sr-only">{textEquivalent}</figcaption>

      <div data-testid="stated-ratio">
        {ratio !== null ? (
          <p className="font-heading text-5xl sm:text-6xl font-bold tracking-tight text-stone-950">
            1 in {ratio}
          </p>
        ) : (
          <p className="font-heading text-2xl font-bold tracking-tight text-stone-950">Not yet classified</p>
        )}
        <p className="mt-1 text-sm text-stone-600">
          postings say anything at all about sponsorship —{" "}
          <span className="font-mono tabular-nums">{fmt(stated)}</span> of{" "}
          <span className="font-mono tabular-nums">{fmt(total)}</span> read.
        </p>
      </div>

      {stated > 0 && (
        <div aria-hidden="true" className="mt-6">
          {/* The composition bar: only the postings that took a position.
              A hairline between segments and real text below mean the split
              still reads with color vision switched off. */}
          <div className="flex h-8 rounded-sm border border-sand overflow-hidden">
            {segments.map((s) => (
              <div
                key={s.key}
                data-testid={`stance-${s.key}`}
                data-emphasis={s.emphasis}
                className={`h-full border-r border-paper last:border-r-0 ${
                  s.emphasis ? "bg-flag" : s.swatch
                }`}
                style={{ width: `${s.widthPct}%` }}
              />
            ))}
          </div>

          {/* One legend, reflowing at `sm`: a stacked list on a narrow
              screen, a single row once there is room, rather than shrinking
              text to fit three labels on one line. */}
          <ul className="mt-3 flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:gap-x-7 sm:gap-y-2">
            {segments.map((s) => (
              <li key={s.key} className="flex items-start gap-2">
                <span aria-hidden="true" className={`mt-1 w-2.5 h-2.5 rounded-sm shrink-0 ${s.emphasis ? "bg-flag" : s.swatch}`} />
                <div>
                  <div className={`text-sm font-medium ${s.emphasis ? "text-flag" : "text-stone-700"}`}>
                    {s.label} · {s.displayPct}%
                  </div>
                  <div className="font-mono tabular-nums text-xs text-stone-500">{fmt(s.count)} postings</div>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      <p className="mt-5 text-xs text-stone-500 leading-snug max-w-md">
        Silence is not a stance — NextOpp shows "not stated" as exactly that, never as a yes.{" "}
        <span className="font-mono tabular-nums">{fmt(not_stated)}</span> postings said nothing, and are not drawn
        in the bar above.
      </p>

      <p className="mt-2 text-xs text-stone-600">
        Counted from live postings in the NextOpp index, classified from each posting's own words. Updated with
        every sweep.
      </p>
    </figure>
  );
}
